import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import Navbar from '../components/Navbar';
import RetroWordmark from '../components/RetroWordmark';
import ImageCard from '../components/ImageCard';
import ImageGridControls from '../components/ImageGridControls';
import { format } from 'date-fns';
import { Link, useLocation } from 'react-router-dom';
import { FaHeart, FaGlobe, FaCoins, FaCaretDown } from 'react-icons/fa';
import { AnimatePresence } from 'framer-motion';

const Drawings = () => {
  const [drawings, setDrawings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('newest');
  const [isHighlightingLikes, setIsHighlightingLikes] = useState(false);
  const [isHighlightingPublic, setIsHighlightingPublic] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const { user, isAuthenticated, getAccessTokenSilently, isLoading: isAuthLoading, logout } = useAuth0();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);

  // Define colors at the component level
  const colors = [
    '#00FF9F', // UI Green
    '#FF1493', // Deep pink
    '#0066FF', // Bright blue
    '#FF6B00', // Orange
    '#FF00FF', // Hot pink
    '#FFB800', // Amber
    '#FF4D00', // Bright orange
    '#FF8A00'  // Golden orange
  ];

  const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)];

  // Export drawings count through localStorage for nav visibility
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('hasDrawings', drawings.length > 0 ? 'true' : 'false');
    }
  }, [drawings.length, isLoading]);

  const handleDelete = async (drawingId) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/drawings/${drawingId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete drawing');
      }

      setDrawings(prevDrawings => {
        const newDrawings = prevDrawings.filter(drawing => drawing.id !== drawingId);
        return newDrawings;
      });
    } catch (error) {
      // Error handling silenced for production
    }
  };

  useEffect(() => {
    const fetchDrawings = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: "retro-api",
            scope: "openid profile email"
          }
        });

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/drawings`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch drawings: ${errorText}`);
        }
        
        const data = await response.json();
        setDrawings(data);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && user?.sub) {
      setIsLoading(true);
      fetchDrawings();
    }
  }, [isAuthenticated, getAccessTokenSilently, user?.sub]);

  // Sort drawings based on sortOrder
  const sortedDrawings = React.useMemo(() => {
    return [...drawings].sort((a, b) => {
      // Use id as fallback since it likely contains a timestamp
      const dateA = a.createdAt || a.timestamp || a.id;
      const dateB = b.createdAt || b.timestamp || b.id;
      
      if (sortOrder === 'newest') {
        return String(dateB).localeCompare(String(dateA));
      } else if (sortOrder === 'oldest') {
        return String(dateA).localeCompare(String(dateB));
      }
      return 0;
    }).map(drawing => ({
      ...drawing,
      prompt: undefined // Explicitly remove the prompt property
    }));
  }, [drawings, sortOrder]);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-[#F4F3F0]">
        <div className="flex justify-center py-12">
          <motion.div 
            className="w-8 h-8 border-2 border-black/20 border-t-black/40 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F4F3F0]">
        <div className="flex justify-center py-12">
          <h2 className="text-2xl font-['VT323'] text-black/60">
            Please log in to view your drawings
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F3F0]">
      <div className="max-w-[920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <ImageGridControls
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            isAuthenticated={isAuthenticated}
            showPublicToggle={false}
            showGrouping={false}
            showFullscreen={false}
            showPopularSort={false}
            showLikesControls={false}
          />
        </div>

        <div className="grid gap-8 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {sortedDrawings.map((drawing) => (
            <motion.div
              key={drawing.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 50,
                mass: 1
              }}
            >
              <ImageCard
                image={drawing}
                onDelete={() => handleDelete(drawing.id)}
                onMouseEnter={() => setHoveredId(drawing.id)}
                onMouseLeave={() => setHoveredId(null)}
                onCopyToPrompt={null}
                onToggleFavorite={null}
                hasBlackBackground={true}
              />
            </motion.div>
          ))}
        </div>

        {/* Cryptic footer */}
        <div className="mt-16 mb-8 text-center">
          <p className="font-['VT323'] text-[14px] text-black/40">
            ✏️ remnants of your drawing sessions • create more while images generate
          </p>
        </div>
      </div>
    </div>
  );
};

export default Drawings; 